import type {BlockSettings, LegacyWidget} from '@wordpress/blocks';
import {CONFIG} from '../../../globals/config';
import Edit from './Edit';
import type {AccordionOptions} from '../Accordion';
import type {StyleOptions} from '../WidgetStyles';
import type {MenuOptions} from './Menu';
import type {DisplayOptions} from './Display';
import {__} from '@wordpress/i18n';
import type {ClosableSections} from '../../../hooks/useOpenSection';

/**
 * @see \Advanced_Sidebar_Menu\Blocks\Common_Attributes::get_common_attributes
 */
type CommonAttr = {
		title?: string;
		style?: object;
		closed_sections?: ClosableSections[];
	}
	& StyleOptions
	& AccordionOptions;

export type WithCommon<T> = T & CommonAttr;

/**
 * @see \Advanced_Sidebar_Menu\Blocks\Common_Attributes::get_server_side_render_attributes
 */
export type ServerSideRenderRequired = {
	isServerSideRenderRequest: boolean;
	clientId: string;
	sidebarId: string;
}

/**
 * Attributes specific to the widget as well as shared
 * widget attributes.
 *
 * @see \Advanced_Sidebar_Menu\Blocks\Block_Abstract::get_all_attributes
 * @see \Advanced_Sidebar_Menu\Blocks\Pages::get_attributes
 */
export type Attr =
	{
		title?: string,
		include_parent: true,
	}
	& DisplayOptions
	& MenuOptions;

export type setAttributes = ( newValue: {
	[attribute in keyof WithCommon<Attr>]?: WithCommon<Attr>[attribute]
} ) => void;

// Wait for basic to be loaded before using the config.
const windowConfig = window.ADVANCED_SIDEBAR_MENU;

/**
 * Attributes used for the example preview.
 * Combines some PRO and basic attributes.
 * The PRO attributes will only be sent if the PRO version is active.
 */
const EXAMPLE: Partial<WithCommon<Attr>> = {
	apply_current_page_parent_styles_to_parent: true,
	apply_current_page_styles_to_parent: true,
	block_style: true,
	border: true,
	border_color: '#333',
	bullet_style: 'none',
	child_page_bg_color: '#666',
	child_page_color: '#fff',
	current_page_bg_color: '#282828',
	current_page_color: '#0cc4c6',
	current_page_font_weight: 'normal',
	current_page_parent_bg_color: '#333',
	grandchild_page_bg_color: '#989898',
	grandchild_page_color: '#282828',
	grandchild_page_font_weight: 'bold',
	navigation_menu: parseInt( Object.keys( CONFIG.menus )[ 0 ] ?? 0 ),
	parent_page_bg_color: '#666',
	parent_page_color: '#fff',
};

export const block = CONFIG.blocks.navigation;

export const name = block.id;

export const settings: BlockSettings<WithCommon<Attr>, '', LegacyWidget<WithCommon<Attr> & {
	title: string
}>> = {
	title: __( 'Advanced Sidebar - Navigation', 'advanced-sidebar-menu' ),
	icon: windowConfig.NavigationIcon ?? 'welcome-widgets-menus',
	category: 'widgets',
	description: __( 'Creates a sidebar menu from a Navigation Menu in Appearance -> Menus', 'advanced-sidebar-menu' ),
	example: {
		attributes: EXAMPLE,
	},
	transforms: {
		from: [
			{
				type: 'block',
				blocks: [ 'core/legacy-widget' ],
				isMatch: ( {idBase, instance}: LegacyWidget<WithCommon<Attr> & {
					title: string
				}> ) => {
					if ( null === instance?.raw ) {
						// Can't transform if a raw instance is not shown in REST API.
						return false;
					}
					return 'advanced-sidebar-navigation-menu' === idBase;
				},
				transform: windowConfig.transformLegacyWidget( name ),
			},
		],
	},
	attributes: windowConfig.translateBlockAttributes( block.attributes ),
	supports: windowConfig.getBlockSupports(),
	edit: props => (
		<Edit {...props} />
	),
	save: () => null,
	apiVersion: 3,
};
